/**
 * MUTAGIM.SHOP — Wishlist Context (Phase 9)
 * Gestion des favoris avec synchronisation serveur
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user, token } = useAuth();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistMap, setWishlistMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [isPublic, setIsPublic] = useState(false);

  // Charger la wishlist quand l'utilisateur se connecte
  useEffect(() => {
    if (user && token) {
      fetchWishlist();
    } else {
      setWishlistItems([]);
      setWishlistMap({});
      setShareToken(null);
      setIsPublic(false);
    }
  }, [user, token]);

  const fetchWishlist = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWishlistItems(data.items || []);
        setShareToken(data.share_token);
        setIsPublic(data.is_public || false);
        
        // Construire la map pour accès rapide
        const map = {};
        data.items?.forEach(item => {
          map[item.product_id] = true;
        });
        setWishlistMap(map);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleWishlist = useCallback(async (productId, productName = '') => {
    if (!token) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/toggle/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.in_wishlist) {
          setWishlistMap(prev => ({ ...prev, [productId]: true }));
          toast.success(productName ? `${productName} ajouté aux favoris` : 'Ajouté aux favoris');
        } else {
          setWishlistMap(prev => {
            const newMap = { ...prev };
            delete newMap[productId];
            return newMap;
          });
          toast.info('Retiré des favoris');
        }
        
        // Refresh la liste complète en background
        fetchWishlist();
        
        return data.in_wishlist;
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error('Erreur lors de la mise à jour');
    }
    return false;
  }, [token]);

  const isInWishlist = useCallback((productId) => {
    return !!wishlistMap[productId];
  }, [wishlistMap]);

  const checkMultiple = useCallback(async (productIds) => {
    if (!token || productIds.length === 0) return {};

    try {
      const response = await fetch(`${API_URL}/api/wishlist/check-multiple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productIds)
      });

      if (response.ok) {
        const data = await response.json();
        setWishlistMap(prev => ({ ...prev, ...data.wishlist_map }));
        return data.wishlist_map;
      }
    } catch (error) {
      console.error('Error checking wishlist:', error);
    }
    return {};
  }, [token]);

  const generateShareLink = async () => {
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/wishlist/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setShareToken(data.share_token);
        setIsPublic(true);
        toast.success('Lien de partage créé !');
        return data.share_url;
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Erreur lors de la création du lien');
    }
    return null;
  };

  const disableSharing = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/wishlist/share`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        setIsPublic(false);
        toast.info('Partage désactivé');
      }
    } catch (error) {
      console.error('Error disabling share:', error);
    }
  };

  const updateNotifications = async (productId, settings) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/wishlist/item/${productId}/notifications`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        // Mettre à jour localement
        setWishlistItems(prev => prev.map(item => 
          item.product_id === productId 
            ? { ...item, ...settings }
            : item
        ));
        toast.success('Préférences mises à jour');
      }
    } catch (error) {
      console.error('Error updating notifications:', error);
    }
  };

  const value = {
    wishlistItems,
    wishlistMap,
    loading,
    shareToken,
    isPublic,
    toggleWishlist,
    isInWishlist,
    checkMultiple,
    generateShareLink,
    disableSharing,
    updateNotifications,
    refreshWishlist: fetchWishlist,
    count: wishlistItems.length,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
}

export default WishlistContext;
